/**
 * Dashboard v2 fixtures — drive the static design pass before the backend exists.
 * PR 4 implements the API to match these shapes (validated by DashboardV2ResponseSchema).
 */
import type {
  DashboardV2Response,
  DashboardV2TimeRange,
  DashboardV2SbomTopRow,
  DashboardV2RedTeamRow,
  RiskBandTrendPoint,
} from '../types';

const isoHoursAgo = (h: number) => new Date(Date.now() - h * 3_600_000).toISOString();
const isoDaysAgo = (d: number) => new Date(Date.now() - d * 86_400_000).toISOString();

// ---- risk-band trend fixture -----------------------------------------------
// sampleApps risk scores: 84 (CRITICAL), 72 (HIGH), 38 (MODERATE), 65 (HIGH), 41 (MODERATE)
// We generate one point per day in the selected time range.  For 24h we still
// show a single day bucket (today) since risk-band trend is always day-grained.
function buildRiskBandTrend(range: DashboardV2TimeRange): RiskBandTrendPoint[] {
  const days = range === '24h' ? 1 : range === '7d' ? 7 : 30;
  return Array.from({ length: days }, (_, i) => {
    // Introduce gentle variation so the chart has visual interest.
    const jitter = Math.sin(i * 1.3) * 4;
    return {
      bucket: new Date(Date.now() - (days - 1 - i) * 86_400_000)
        .toISOString()
        .replace(/T.*$/, 'T00:00:00.000Z'),   // UTC midnight
      // CRITICAL: app-1 (84) → avg ~84 ± jitter
      critical_avg:   Math.max(76, Math.round((84 + jitter) * 10) / 10),
      critical_count: 1,
      // HIGH: app-2 (72) + app-4 (65) → avg ~68.5 ± jitter
      high_avg:   Math.max(51, Math.round(((72 + 65) / 2 + jitter) * 10) / 10),
      high_count: 2,
      // MODERATE: app-3 (38) + app-5 (41) → avg ~39.5 ± jitter
      medium_avg:   Math.max(26, Math.round(((38 + 41) / 2 + jitter * 0.5) * 10) / 10),
      medium_count: 2,
      // LOW: none in sample apps (would need score ≤25)
      low_avg:   0.0,
      low_count: 0,
      // overall_avg: flat mean of all individual raw signals (matches KPI formula)
      // sample: 84 + 72 + 65 + 38 + 41 = 300 / 5 signals ≈ 60 ± jitter
      overall_avg: Math.round(((84 + 72 + 65 + 38 + 41) / 5 + jitter) * 10) / 10,
    };
  });
}

const sampleApps: DashboardV2Response['applications'] = [
  { id: 'app-1', name: 'Customer-Support-Bot', status: 'CRITICAL', owner: 'AI Platform Team', risk_score: 84, coverage_pct: 88 },
  { id: 'app-2', name: 'Sales-Copilot',        status: 'AT_RISK',  owner: 'Revenue Eng',      risk_score: 72, coverage_pct: 91 },
  { id: 'app-3', name: 'Doc-Summarizer',       status: 'HEALTHY',  owner: 'Knowledge',         risk_score: 38, coverage_pct: 96 },
  { id: 'app-4', name: 'Internal-Code-Review', status: 'AT_RISK',  owner: 'DevOps',            risk_score: 65, coverage_pct: 84 },
  { id: 'app-5', name: 'Marketing-Writer',     status: 'HEALTHY',  owner: 'Growth',            risk_score: 41, coverage_pct: 95 },
];

// 24h hourly series — matches design §6.1 chart (spikes around 08:00 / 16:00)
const trend24h: DashboardV2Response['timeseries'] = Array.from({ length: 24 }, (_, i) => {
  const hour = i;
  const spike = hour === 8 ? 1.0 : hour === 16 ? 0.85 : 0.0;
  const base = 40 + Math.round(60 * Math.sin((hour / 24) * Math.PI));
  return {
    bucket: isoHoursAgo(24 - i),
    scans: Math.max(20, Math.round(base + spike * 200)),
    critical: Math.round(spike * 6 + (hour % 7 === 0 ? 1 : 0)),
    high:     Math.round(spike * 10 + (hour % 5 === 0 ? 2 : 1)),
    medium:   Math.round(base / 8),
    low:      Math.round(base / 5),
  };
});

const trend7d: DashboardV2Response['timeseries'] = Array.from({ length: 7 }, (_, i) => ({
  bucket: isoDaysAgo(6 - i),
  scans: 320 + i * 35 + (i === 4 ? 180 : 0),
  critical: 2 + (i === 4 ? 6 : i % 2),
  high:     8 + (i === 4 ? 14 : i),
  medium:   16 + i * 2,
  low:      22 + i,
}));

const trend30d: DashboardV2Response['timeseries'] = Array.from({ length: 30 }, (_, i) => ({
  bucket: isoDaysAgo(29 - i),
  scans: 180 + Math.round(60 * Math.sin(i / 4)) + (i === 22 ? 220 : 0),
  critical: Math.max(0, Math.round(2 + Math.sin(i / 3) * 2) + (i === 22 ? 5 : 0)),
  high:     Math.max(0, Math.round(6 + Math.sin(i / 5) * 4) + (i === 22 ? 9 : 0)),
  medium:   12 + Math.round(Math.sin(i / 2) * 6),
  low:      18 + Math.round(Math.cos(i / 3) * 8),
}));

const seriesForRange = (range: DashboardV2TimeRange) =>
  range === '24h' ? trend24h : range === '7d' ? trend7d : trend30d;

const sumSeries = (s: DashboardV2Response['timeseries']) =>
  s.reduce(
    (acc, p) => ({
      scans:    acc.scans + p.scans,
      critical: acc.critical + p.critical,
      high:     acc.high + p.high,
      medium:   acc.medium + p.medium,
      low:      acc.low + p.low,
    }),
    { scans: 0, critical: 0, high: 0, medium: 0, low: 0 },
  );

export function buildDashboardV2Fixture(
  range: DashboardV2TimeRange,
  appIds: string[] = [],
): DashboardV2Response {
  const filtered = appIds.length === 0 ? sampleApps : sampleApps.filter(a => appIds.includes(a.id));
  const apps = filtered.length === 0 ? sampleApps : filtered;
  const series = seriesForRange(range);
  const totals = sumSeries(series);
  const scale = apps.length / sampleApps.length;

  const avgRisk = apps.length
    ? Math.round(apps.reduce((s, a) => s + a.risk_score, 0) / apps.length)
    : 0;
  const avgCoverage = apps.length
    ? Math.round(apps.reduce((s, a) => s + a.coverage_pct, 0) / apps.length)
    : 0;

  return {
    kpis: {
      total_apps: apps.length === sampleApps.length ? 142 : apps.length,
      total_apps_delta_week: 12,
      avg_risk_score: avgRisk,
      avg_risk_score_delta_week: -4,
      coverage_pct: avgCoverage,
      applied_policies: Math.round(4_210 * scale),
      validation_findings: { critical: Math.round(totals.critical * 0.5), high: Math.round(totals.high * 0.6) },
      vulnerabilities:     { critical: Math.round(totals.critical * 0.3), high: Math.round(totals.high * 0.4) },
    },
    timeseries: series,
    risk_band_trend: buildRiskBandTrend(range),
    pillars: {
      static: {
        sbom_components: Math.round(1_450 * scale),
        vulnerabilities: { critical: Math.round(totals.critical * 0.3), high: Math.round(totals.high * 0.4) },
        total_vulnerabilities: Math.round(totals.critical * 0.3) + Math.round(totals.high * 0.4) + Math.round(totals.high * 0.3),
        risk_score: 62,
        top_components: [
          { name: 'gpt-4o',          node_type: 'Model',     severity: 'HIGH',   cve_count: 3 },
          { name: 'LangChain',       node_type: 'Framework', severity: 'MEDIUM', cve_count: 1 },
          { name: 'text-embedding-3-small', node_type: 'EmbeddingModel', severity: null, cve_count: 0 },
          { name: 'Pinecone',        node_type: 'Datastore', severity: null,     cve_count: 0 },
        ],
      },
      policy: {
        applied: 180,
        violations: { critical: Math.round(totals.critical * 0.15), high: Math.round(totals.high * 0.25) },
        violations_total: Math.round(totals.critical * 0.15) + Math.round(totals.high * 0.25) + 8,
        risk_score: 72,
        violation_types: [
          { label: 'PII Data Leakage',                count: 12, severity: 'CRITICAL' },
          { label: 'Prompt Injection',                count: 9,  severity: 'CRITICAL' },
          { label: 'Unauthorized System Override',    count: 4,  severity: 'HIGH' },
          { label: 'Excessive Agency',                count: 3,  severity: 'HIGH' },
          { label: 'Insecure Output Handling',        count: 2,  severity: 'MEDIUM' },
        ],
      },
      runtime: {
        validations_total: Math.round(1_200_000 * scale),
        behavior_findings: { critical: Math.round(totals.critical * 0.5), high: Math.round(totals.high * 0.6) },
        behavior_risk_score: 48,
        redteam:           { critical: Math.round(totals.critical * 0.18), high: Math.round(totals.high * 0.22) },
        redteam_risk_score: 62,
      },
    },
    applications: apps,
    time_range: range,
    data_as_of: new Date().toISOString(),
  };
}

export const emptyDashboardV2Fixture: DashboardV2Response = {
  kpis: {
    total_apps: 0,
    total_apps_delta_week: 0,
    avg_risk_score: 0,
    avg_risk_score_delta_week: 0,
    coverage_pct: 0,
    applied_policies: 0,
    validation_findings: { critical: 0, high: 0 },
    vulnerabilities:     { critical: 0, high: 0 },
  },
  timeseries: [],
  risk_band_trend: [],
  pillars: {
    static:  { sbom_components: 0, vulnerabilities: { critical: 0, high: 0 }, total_vulnerabilities: 0, risk_score: 0, top_components: [] },
    policy:  { applied: 0, violations: { critical: 0, high: 0 }, violations_total: 0, risk_score: 0, violation_types: [] },
    runtime: { validations_total: 0, behavior_findings: { critical: 0, high: 0 }, behavior_risk_score: 0, redteam: { critical: 0, high: 0 }, redteam_risk_score: 0 },
  },
  applications: [],
  time_range: '7d',
  data_as_of: new Date().toISOString(),
};

export const sbomTopFixture: DashboardV2SbomTopRow[] = [
  { component: 'gpt-4o',                   version: null,    cve: null,             severity: null,       node_type: 'Model',          cve_count: 0 },
  { component: 'text-embedding-3-small',   version: null,    cve: null,             severity: null,       node_type: 'EmbeddingModel', cve_count: 0 },
  { component: 'LangChain',                version: '0.0.1', cve: 'CVE-2023-39631', severity: 'CRITICAL', node_type: 'Framework',      cve_count: 2 },
  { component: 'FastAPI',                  version: '0.95.0',cve: null,             severity: null,       node_type: 'Framework',      cve_count: 0 },
  { component: 'Pinecone',                 version: null,    cve: null,             severity: null,       node_type: 'Datastore',      cve_count: 0 },
  { component: 'PostgreSQL',               version: '15.0',  cve: 'CVE-2023-2455',  severity: 'HIGH',     node_type: 'Datastore',      cve_count: 1 },
];

export const redTeamFixture: DashboardV2RedTeamRow[] = [
  { attack: 'Prompt injection detected',        category: 'Static + Dynamic',  severity: 'CRITICAL', succeeded_at: isoHoursAgo(2),  source: 'Behavior' },
  { attack: 'PII exposure in response',         category: 'Static',            severity: 'HIGH',     succeeded_at: isoHoursAgo(5),  source: 'Behavior' },
  { attack: 'DAN jailbreak',                    category: 'Jailbreak',         severity: 'CRITICAL', succeeded_at: isoHoursAgo(7),  source: 'Red-Team' },
  { attack: 'Prompt leak via role-play',        category: 'Prompt Injection',  severity: 'HIGH',     succeeded_at: isoHoursAgo(11), source: 'Red-Team' },
  { attack: 'System prompt override',           category: 'Prompt Injection',  severity: 'HIGH',     succeeded_at: isoDaysAgo(1),   source: 'Red-Team' },
];
